import React, { useState, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const EmailTemplateEditor = ({ 
  templateHtml, 
  onTemplateChange, 
  equipmentFields, 
  onEquipmentFieldsChange,
  includeEquipmentTable,
  onIncludeEquipmentTableChange,
  tableConfig,
  onTableConfigChange,
  customVariables,
  onCustomVariablesChange 
}) => {
  const [showVariableModal, setShowVariableModal] = useState(false);
  const [newVariableName, setNewVariableName] = useState('');
  const [newVariableValue, setNewVariableValue] = useState('');
  
  // Available equipment fields with human-readable labels
  const availableFields = [
    { value: 'safety_equipment_type__name', label: 'Equipment Type' },
    { value: 'safety_equipment_serial_number', label: 'Serial Number' },
    { value: 'safety_equipment_status', label: 'Status' },
    { value: 'safety_equipment_model__name', label: 'Model' },
    { value: 'safety_equipment_model__manufacturer', label: 'Manufacturer' },
    { value: 'safety_equipment_inspection_date', label: 'Last Inspection Date' },
    { value: 'safety_equipment_next_inspection_date', label: 'Next Inspection Date' },
    { value: 'safety_equipment_purchase_date', label: 'Purchase Date' },
    { value: 'safety_equipment_notes', label: 'Notes' },
    { value: 'company__name', label: 'Company Name' },
    { value: 'created_date', label: 'Created Date' },
    { value: 'updated_date', label: 'Last Updated' }
  ];

  // Quill editor configuration
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean'],
        ['variable-button'] // Custom button for variables
      ],
      handlers: {
        'variable-button': handleInsertVariable
      }
    }
  }), []);

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'align',
    'link', 'image'
  ];

  function handleInsertVariable() {
    // Show dropdown with available variables
    const variables = Object.keys(customVariables);
    if (variables.length === 0) {
      alert('No custom variables defined. Add variables first.');
      return;
    }
    
    const selectedVariable = window.prompt(
      `Available variables:\n${variables.map((v, i) => `${i+1}. ${v}`).join('\n')}\n\nEnter variable name:`
    );
    
    if (selectedVariable && variables.includes(selectedVariable)) {
      const quill = this.quill;
      const range = quill.getSelection();
      quill.insertText(range.index, `{{${selectedVariable}}}`, 'user');
    }
  }

  const handleFieldToggle = (field) => {
    const updatedFields = equipmentFields.includes(field)
      ? equipmentFields.filter(f => f !== field)
      : [...equipmentFields, field];
    onEquipmentFieldsChange(updatedFields);
  };

  const addCustomVariable = () => {
    if (!newVariableName.trim()) return;
    
    const updatedVariables = {
      ...customVariables,
      [newVariableName]: newVariableValue
    };
    onCustomVariablesChange(updatedVariables);
    setNewVariableName('');
    setNewVariableValue('');
    setShowVariableModal(false);
  };

  const removeCustomVariable = (varName) => {
    const updatedVariables = { ...customVariables };
    delete updatedVariables[varName];
    onCustomVariablesChange(updatedVariables);
  };

  const insertVariable = (varName) => {
    const newHtml = templateHtml + `{{${varName}}}`;
    onTemplateChange(newHtml);
  };

  return (
    <div className="space-y-6">
      {/* Equipment Table Configuration */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <h4 className="font-medium text-gray-900 mb-3">Equipment Information</h4>
        
        <label className="flex items-center mb-4">
          <input
            type="checkbox"
            checked={includeEquipmentTable}
            onChange={(e) => onIncludeEquipmentTableChange(e.target.checked)}
            className="mr-2"
          />
          Include equipment table in email
        </label>

        {includeEquipmentTable && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Equipment Fields to Include:
              </label>
              <div className="grid grid-cols-2 gap-2">
                {availableFields.map(field => (
                  <label key={field.value} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={equipmentFields.includes(field.value)}
                      onChange={() => handleFieldToggle(field.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">{field.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Table Styling */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Border Color
                </label>
                <input
                  type="color"
                  value={tableConfig.border_color}
                  onChange={(e) => onTableConfigChange({
                    ...tableConfig,
                    border_color: e.target.value
                  })}
                  className="w-full h-10 rounded"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Header Background
                </label>
                <input
                  type="color"
                  value={tableConfig.header_bg_color}
                  onChange={(e) => onTableConfigChange({
                    ...tableConfig,
                    header_bg_color: e.target.value
                  })}
                  className="w-full h-10 rounded"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Custom Variables */}
      <div className="border rounded-lg p-4 bg-blue-50">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-medium text-gray-900">Custom Variables</h4>
          <button
            onClick={() => setShowVariableModal(true)}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Add Variable
          </button>
        </div>
        
        <div className="space-y-2">
          {Object.entries(customVariables).map(([name, value]) => (
            <div key={name} className="flex items-center justify-between bg-white p-2 rounded">
              <span className="font-mono text-sm">{`{{${name}}} = ${value}`}</span>
              <div className="flex space-x-2">
                <button
                  onClick={() => insertVariable(name)}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Insert
                </button>
                <button
                  onClick={() => removeCustomVariable(name)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
          
          {/* Default template variables */}
          <div className="border-t pt-2 mt-2">
            <p className="text-sm text-gray-600 mb-2">Available Template Variables:</p>
            <div className="space-y-1">
              <div className="flex items-center justify-between bg-white p-2 rounded opacity-75">
                <span className="font-mono text-sm">{'{{EQUIPMENT_TABLE}}'} = Equipment details table</span>
                <button
                  onClick={() => insertVariable('EQUIPMENT_TABLE')}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Insert
                </button>
              </div>
              <div className="flex items-center justify-between bg-white p-2 rounded opacity-75">
                <span className="font-mono text-sm">{'{{SERVICE_DATE}}'} = Service date</span>
                <button
                  onClick={() => insertVariable('SERVICE_DATE')}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Insert
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WYSIWYG Editor */}
      <div className="border rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email Template (HTML)
        </label>
        <div className="bg-white rounded">
          <ReactQuill
            theme="snow"
            value={templateHtml}
            onChange={onTemplateChange}
            modules={modules}
            formats={formats}
            style={{ minHeight: '300px' }}
          />
        </div>
        
        <div className="mt-2 text-sm text-gray-500">
          <p>Tip: Use variables like {'{'}{'{'}{'}'}COMPANY_NAME{'{'}{'}'}{'}'}' and {'{'}{'{'}{'}'}EQUIPMENT_TABLE{'{'}{'}'}{'}'}' in your template.</p>
          <p>The {'{'}{'{'}{'}'}EQUIPMENT_TABLE{'{'}{'}'}{'}'}' will be replaced with a formatted table of selected equipment fields.</p>
        </div>
      </div>

      {/* Preview */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <h4 className="font-medium text-gray-900 mb-2">Preview</h4>
        <div 
          className="bg-white p-4 rounded border min-h-32"
          dangerouslySetInnerHTML={{ __html: templateHtml }}
        />
      </div>

      {/* Variable Modal */}
      {showVariableModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowVariableModal(false)} />
            
            <div className="relative bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">Add Custom Variable</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Variable Name
                  </label>
                  <input
                    type="text"
                    value={newVariableName}
                    onChange={(e) => setNewVariableName(e.target.value.toUpperCase())}
                    placeholder="COMPANY_ADDRESS"
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Default Value
                  </label>
                  <input
                    type="text"
                    value={newVariableValue}
                    onChange={(e) => setNewVariableValue(e.target.value)}
                    placeholder="123 Main St, City, Country"
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowVariableModal(false)}
                  className="px-4 py-2 text-gray-700 border rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={addCustomVariable}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add Variable
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplateEditor;